import Box, { BoxProps } from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import _ from 'lodash';
import { isValidElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import helpers from '../../../helpers';
import { deletePlugin } from '../../../lib/k8s/apiProxy';
import { ConfigStore } from '../../../plugin/configStore';
import { PluginInfo, reloadPage } from '../../../plugin/pluginsSlice';
import { useTypedSelector } from '../../../redux/reducers/reducers';
import NotFoundComponent from '../../404';
import { SectionBox } from '../../common';
import { ConfirmDialog } from '../../common/Dialog';
import ErrorBoundary from '../../common/ErrorBoundary';

const PluginSettingsDetailsInitializer = (props: { plugin: PluginInfo }) => {
  const { plugin } = props;
  const store = new ConfigStore(plugin.name);
  const pluginConf = store.useConfig();
  const config = pluginConf() as { [key: string]: any };

  function handleSave(data: { [key: string]: any }) {
    store.set(data);
  }

  function handleDeleteConfirm() {
    const name = plugin.name.split('/').splice(-1)[0];
    deletePlugin(name)
      .then(() => {
        // update the plugin list
        const dispatch = useDispatch();
        dispatch(reloadPage());

        // @todo error is not handled here.
      })
      .finally(() => {
        // redirect /plugins page
        window.location.pathname = '/settings/plugins';
      });
  }

  return (
    <PluginSettingsDetailsPure
      config={config}
      plugin={plugin}
      onSave={handleSave}
      onDelete={handleDeleteConfirm}
    />
  );
};

export default function PluginSettingsDetails() {
  const pluginSettings = useTypedSelector(state => state.plugins.pluginSettings);
  const { name } = useParams<{ name: string }>();

  const plugin = useMemo(() => {
    const decodedName = decodeURIComponent(name);
    return pluginSettings.find(plugin => plugin.name === decodedName);
  }, [pluginSettings, name]);

  if (!plugin) {
    return <NotFoundComponent />;
  }

  return <PluginSettingsDetailsInitializer plugin={plugin} />;
}

const ScrollableBox = (props: BoxProps) => (
  <Box
    sx={{
      overflowY: 'scroll',
      msOverflowStyle: 'none',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
    }}
    {...props}
  />
);

/**
 * Represents the properties expected by the PluginSettingsDetails component.
 *
 * @property {Object} [config] - Optional configuration settings for the plugin. This is an object that contains current configuration of the plugin.
 * @property {PluginInfo} plugin - Information about the plugin.
 * @property {(data: { [key: string]: any }) => void} [onSave] - Optional callback function that is called when the settings are saved. The function receives an object representing the updated configuration settings for the plugin.
 * @property {() => void} onDelete - Callback function that is called when the plugin is requested to be deleted. This function does not take any parameters and does not return anything.
 *
 * @see PluginInfo - Refer to the PluginInfo documentation for details on what this object should contain.
 */
export interface PluginSettingsDetailsPureProps {
  config?: { [key: string]: any };
  plugin: PluginInfo;
  onSave?: (data: { [key: string]: any }) => void;
  onDelete: () => void;
}

export function PluginSettingsDetailsPure(props: PluginSettingsDetailsPureProps) {
  const { config, plugin, onSave, onDelete } = props;
  const { t } = useTranslation(['translation']);
  const [data, setData] = useState<{ [key: string]: any } | undefined>(config);
  const [enableSaveButton, setEnableSaveButton] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const history = useHistory();

  useEffect(() => {
    if (!_.isEqual(config, data)) {
      setEnableSaveButton(true);
    } else {
      setEnableSaveButton(false);
    }
  }, [data, config]);

  function onDataChange(data: { [key: string]: any }) {
    setData(data);
  }

  async function handleSave() {
    if (onSave && data) {
      await onSave(data);
      history.push('/settings/plugins');
    }
  }

  function handleDelete() {
    setOpenDeleteDialog(true);
  }

  function handleDeleteConfirm() {
    onDelete();
  }

  async function handleCancel() {
    await setData(config);
    history.push('/settings/plugins');
  }

  let component;
  if (isValidElement(plugin.settingsComponent)) {
    component = plugin.settingsComponent;
  } else if (typeof plugin.settingsComponent === 'function') {
    const Comp = plugin.settingsComponent;
    if (plugin.displaySettingsComponentWithSaveButton) {
      component = <Comp onDataChange={onDataChange} data={data} />;
    } else {
      component = <Comp />;
    }
  } else {
    component = null;
  }

  return (
    <>
      <SectionBox aria-live="polite" title={plugin.name} backLink={'/settings/plugins'}>
        {plugin.description}
        <ScrollableBox style={{ height: '70vh' }} py={0}>
          <ConfirmDialog
            open={openDeleteDialog}
            title={t('translation|Delete Plugin')}
            description={t('translation|Are you sure you want to delete this plugin?')}
            handleClose={() => setOpenDeleteDialog(false)}
            onConfirm={() => handleDeleteConfirm()}
          />
          <ErrorBoundary>{component}</ErrorBoundary>
        </ScrollableBox>
      </SectionBox>
      <Box py={0}>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          alignItems="center"
          sx={{ borderTop: '2px solid', borderColor: 'silver', padding: '10px' }}
        >
          <Stack direction="row" spacing={1}>
            {plugin.displaySettingsComponentWithSaveButton && (
              <>
                <Button
                  variant="contained"
                  disabled={!enableSaveButton}
                  style={{ backgroundColor: 'silver', color: 'black' }}
                  onClick={handleSave}
                >
                  {t('translation|Save')}
                </Button>
                <Button style={{ color: 'silver' }} onClick={handleCancel}>
                  {t('translation|Cancel')}
                </Button>
              </>
            )}
          </Stack>
          {helpers.isElectron() ? (
            <Button variant="text" color="error" onClick={handleDelete}>
              {t('translation|Delete Plugin')}
            </Button>
          ) : null}
        </Stack>
      </Box>
    </>
  );
}
